import { useEffect, useRef, useState } from 'react'
import { Form, Formik } from 'formik'
import autoAnimate from '@formkit/auto-animate'
import cpfMask from '@mobi/utils/masks/cpf'
import cnpjMask from '@mobi/utils/masks/cnpj'
import analytics from '@mobi/libraries/analytics'
import If from '@mobi/components/If'
import removeSpecialChars from '@mobi/utils/formatters/removeSpecialChars'
import { addNRPageView, trackNRPage } from 'utils/trackings/newRelicEvents'
import { formIdRegister } from 'utils/trackings/constants'
import { isValidCpfCnpj } from 'utils/fields/verifyIfIsCpfCnpj'
import * as ROUTES from 'Routes'
import Icon from 'components/Icon'
import Text from 'components/Text'
import Button from 'components/Button'
import Accordion from 'components/Accordion'
// import DocumentAutoFill from 'components/DocumentAutoFill'
import InputTextAnimation from 'components/Fields/InputText/InputTextAnimation'
import useLandingRegister from 'pages/LandingRegister/useLandingRegister'
import getMask from 'utils/fields/masks'
import { ROOT_URL } from 'gatsby-env-variables'
import './LandingHeroForm.scss'
import classNames from 'classnames'

const FormAutoCompleteWrapper = ({
  formProps: { setValues },
  document,
  formAutoCompleteValues,
  isSameDocument,
}) => {
  const hasDocument = document?.length

  useEffect(() => {
    if (hasDocument && isSameDocument) {
      setValues({
        document: formAutoCompleteValues?.document,
        name: formAutoCompleteValues?.name,
        email: formAutoCompleteValues?.email,
        cellphone: formAutoCompleteValues?.cellphone,
        birth_date: formAutoCompleteValues?.birth_date,
      })
    }
  }, [document, formAutoCompleteValues])

  return null
}

const LandingHeroForm = ({
  location,
  isHeroFormOpen,
  setIsHeroFormOpen,
  refRecaptchaReneg,
  formikRef = null,
}) => {
  const documentRef = useRef(null)
  const nameRef = useRef(null)
  const emailRef = useRef(null)
  const dateRef = useRef(null)
  const phoneRef = useRef(null)

  const [isValidDocument, setIsValidDocument] = useState(false)

  const isRegisterPage = location?.pathname.includes(ROUTES.register.path)

  const formSecondStep = useRef(null)

  const {
    onSubmit,
    isCaptchaValid,
    birthdayNotValid,
    disableButton,
    formAutoCompleteValues,
    setBirthdayNotValid,
    setFormUserInfo,
  } = useLandingRegister({
    location,
  })

  const [isCNPJDocument, setIsCNPJDocument] = useState(
    location?.state?.document?.length > 14
  )

  const [isAutoFillFields, setAutoFillFields] = useState(false)

  useEffect(() => {
    if (isAutoFillFields) {
      analytics.track('elementViewed', {
        text: 'register_autocomplete',
      })
    }
  }, [isAutoFillFields])

  useEffect(() => {
    if (!isRegisterPage && isValidDocument && !isHeroFormOpen) {
      handleAdvanceStep()
    }
  }, [documentRef, isHeroFormOpen, isValidDocument])

  useEffect(() => {
    formSecondStep.current && autoAnimate(formSecondStep.current)
  }, [formSecondStep])

  const handleTracking = (ev) => {
    const elementClickedPayload = {
      location: ev.currentTarget.dataset.trackingLocation,
      position: ev.currentTarget.dataset.trackingPosition,
      elementType: ev.currentTarget.dataset.trackingElement,
      text: ev.currentTarget.textContent || ev.currentTarget.ariaLabel,
      htmlId: ev.currentTarget.dataset.unique,
      actionOutcome: ev.currentTarget.dataset.trackingActionoutcome,
      outboundUrl: ev.currentTarget.dataset.trackingOutboundurl,
    }

    analytics.track('elementClicked', elementClickedPayload)
    analytics.track('segmentButtonClick', {
      name: elementClickedPayload.text,
      id: elementClickedPayload.htmlId,
    })
  }

  const handleAdvanceStep = () => {
    const formViewedPayload = {
      formId: formIdRegister,
    }

    if (isValidDocument) {
      setIsHeroFormOpen(true)
      analytics.track('elementViewed', {
        text: 'formSecondStepStarted',
      })

      const documentValue = removeSpecialChars(
        documentRef.current?.props?.value
      )

      analytics.track('fieldInputted', {
        fieldValue: btoa(documentValue),
        fieldLabel: 'cpf_home',
      })

      analytics.track('formViewed', formViewedPayload)
      addNRPageView('lp_registration')
      analytics.track('gaPageLoad', { pageName: 'PaginadeRegistro' })
      analytics.track('segmentPageView', 'Registro')
      trackNRPage('Registro')
      analytics.track('segmentElementViewed', 'CardExpansivo')
    }
  }

  const renderLegalBlok = () => (
    <div className="register-bottom-part__legal-blok">
      <Text p size={11}>
        Ao continuar, autorizo a gestão de banco de dados do cadastro positivo a
        disponibilizarem ao Itaú Unibanco S.A o meu histórico de crédito. Estou
        ciente dos{' '}
        <a
          href="https://www.itau.com.br/seguranca/termo-autorizacao"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/seguranca/termo-autorizacao"
          onClick={handleTracking}
        >
          termos de autorização
        </a>{' '}
        e que poderei efetuar o{' '}
        <a
          href="https://www.itau.com.br/cadastro-positivo"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/cadastro-positivo"
          onClick={handleTracking}
        >
          cancelamento
        </a>{' '}
        a qualquer momento.
      </Text>

      <Text p size={11}>
        Autorizo também a consulta ao{' '}
        <a
          href="https://www.itau.com.br/seguranca/sistema-informacao-credito"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/seguranca/sistema-informacao-credito"
          onClick={handleTracking}
        >
          sistema de informações de crédito
        </a>
        (SCR) do Banco Central, estou ciente que meus dados serão registrados no
        SCR e de acordo com a{' '}
        <a
          href="https://www.itau.com.br/privacidade"
          target="_blank"
          rel="noreferrer"
          data-tracking-location="form"
          data-tracking-position="form-register"
          data-tracking-actionoutcome="redirect"
          data-tracking-element="link"
          data-tracking-outboundurl="https://www.itau.com.br/privacidade"
          onClick={handleTracking}
        >
          política de privacidade.
        </a>
      </Text>
    </div>
  )

  return (
    <div
      className={classNames('register-form-input', {
        'register-form-input__register-page': isRegisterPage,
      })}
    >
      <Formik
        innerRef={formikRef}
        initialValues={{
          document: '',
          name: '',
          email: '',
          cellphone: '',
          birth_date: '',
        }}
        onSubmit={async (values) => {
          analytics.track('segmentButtonClick', {
            name: 'Consultar:Form',
            id: 'cd3fc42e-118b-40a0-ab2d-d9fd8e25a44e',
          })
          setFormUserInfo(values)
          onSubmit(values, refRecaptchaReneg, {
            source_url: location.href,
          })
        }}
      >
        {({ isValid, values, errors, ...formProps }) => {
          const isValidateDocument =
            !errors.document && isValidCpfCnpj(values.document)
          setIsValidDocument(isValidateDocument)

          const valuesWithoutBirthDate = { ...values, birth_date: null }
          const disableButtonForm = isCNPJDocument
            ? Object.values(valuesWithoutBirthDate).includes('')
            : Object.values(values).includes('')

          const handleEnter = (event, nextInput) => {
            if (!nextInput || isValid) return

            if (event.key === 'Enter') {
              event.preventDefault()

              nextInput.current.inputElement.focus()
            }
          }

          const renderFirstStep = () => (
            <InputTextAnimation
              name="document"
              label="CPF ou CNPJ"
              dataUnique="ddfd903e-1617-4f1d-aed8-ded6b73ab4f0"
              trackingFieldlabel="CPF ou CNPJ"
              dataTest="input-document"
              trackingFieldtype="DOCUMENT"
              ref={documentRef}
              mask={(e) => {
                if (e && removeSpecialChars(e).length <= 11) return cpfMask
                else return cnpjMask
              }}
              rules={{ isCpfOrCnpj: true, isRequired: true }}
              inputMode="numeric"
              errors={errors}
              {...formProps}
              handleChange={(ev) => {
                const value = removeSpecialChars(ev.target.value)
                formProps.setFieldValue('document', ev.target.value)
                setIsCNPJDocument(value.length > 11)
              }}
              isAutoFillFields={isAutoFillFields}
              onKeyDown={
                isRegisterPage ? (event) => handleEnter(event, nameRef) : null
              }
            />
          )

          const renderSecondStep = () => (
            <>
              {isSameDocumentToAutofill ? (
                <Text
                  p
                  size={6}
                  className="register-form-content__autofill-message-text"
                >
                  Para mais agilidade, preenchemos os campos com os dados do seu
                  último acesso.
                </Text>
              ) : null}
              <InputTextAnimation
                name="name"
                label="Nome completo"
                rules={{ isName: true, isRequired: true }}
                dataTest="input-name"
                dataUnique="97baf47c-178d-4868-a31f-38fe193ec9a3"
                trackingFieldlabel="Nome completo"
                trackingFieldtype="FULLNAME"
                ref={nameRef}
                errors={errors}
                onKeyDown={(event) => handleEnter(event, emailRef)}
                {...formProps}
                isAutoFillFields={isAutoFillFields}
              />

              {!isCNPJDocument ? (
                <InputTextAnimation
                  name="birth_date"
                  label="Data de nascimento"
                  mask={() => getMask('date')}
                  inputMode="numeric"
                  rules={{ isDate: true, isRequired: true }}
                  dataTest="input-birthdate"
                  dataUnique="9db49595-5c1e-4355-bc28-3965721de06c"
                  trackingFieldlabel="Data de nascimento"
                  trackingFieldtype="DOB"
                  ref={dateRef}
                  errors={errors}
                  {...formProps}
                  handleChange={(ev) => {
                    setBirthdayNotValid('')
                    formProps.handleChange(ev)
                  }}
                  onKeyDown={(event) => handleEnter(event, phoneRef)}
                  isAutoFillFields={isAutoFillFields}
                />
              ) : null}
              <InputTextAnimation
                name="email"
                label="E-mail"
                type="email"
                rules={{ isEmail: true, isRequired: true }}
                dataTest="input-email"
                dataUnique="d9751ae2-d253-4eeb-a083-86f9f543bc46"
                trackingFieldlabel="E-mail"
                trackingFieldtype="EMAIL"
                ref={emailRef}
                errors={errors}
                onKeyDown={(event) => handleEnter(event, dateRef)}
                isAutoFillFields={isAutoFillFields}
                {...formProps}
              />
              <InputTextAnimation
                name="cellphone"
                label="Celular"
                type="tel"
                mask={() => getMask('phone')}
                rules={{ isPhone: true, isRequired: true }}
                dataTest="input-cellphone"
                dataUnique="6b5fa8e3-8e17-447d-aef5-0c6ad833b565"
                trackingFieldlabel="Celular"
                trackingFieldtype="CONTACT"
                ref={phoneRef}
                errors={errors}
                isAutoFillFields={isAutoFillFields}
                {...formProps}
              />
              <div className="register-form-button-container">
                <Button
                  id="hero-button"
                  className="button"
                  type="submit"
                  isDisabled={
                    disableButtonForm ||
                    (!isCaptchaValid && !isValid) ||
                    disableButton
                  }
                  data-unique="9c06d484-324b-445d-92c5-dec5f8f14285"
                  data-test="button-submit-personal-data"
                  data-tracking-location="form"
                  data-tracking-position="form-register"
                  data-tracking-actionoutcome="redirect"
                  data-tracking-element="button"
                  data-tracking-outboundurl={`${ROOT_URL}${ROUTES.ofertas.path}?step=ofertas`}
                >
                  Consultar
                </Button>
              </div>
              {isValid && birthdayNotValid && (
                <div
                  className="register-form-invalid-container"
                  data-unique="c44fa577-2387-4348-8643-4e8b2adcf684"
                >
                  <div className="icon">
                    <Icon iconId="iconWarning" />
                  </div>
                  <div className="text">{birthdayNotValid}</div>
                </div>
              )}
            </>
          )

          const isSameDocumentToAutofill =
            removeSpecialChars(values.document) ===
            (formAutoCompleteValues?.document &&
              removeSpecialChars(formAutoCompleteValues?.document))

          setAutoFillFields(isSameDocumentToAutofill)

          return (
            <Form
              className={classNames('register-form-content', {
                'register-form-content__register-page': isRegisterPage,
              })}
            >
              {/* {ENV !== 'prod' && <DocumentAutoFill formProps={formProps} />} */}
              <FormAutoCompleteWrapper
                formProps={formProps}
                document={values.document}
                formAutoCompleteValues={formAutoCompleteValues}
                isSameDocument={isSameDocumentToAutofill}
              />
              <If
                condition={!isRegisterPage}
                renderIf={
                  <>
                    {renderFirstStep()}
                    {!isHeroFormOpen && (
                      <div className="register-form-button-container">
                        <Button
                          className="button"
                          type="submit"
                          isDisabled={disableButtonForm || disableButton}
                          data-unique="36f77fbb-6a7a-4808-9040-f1974f804f73"
                          data-test="button-advance-lp-form"
                          data-tracking-location="form"
                          data-tracking-position="form-register"
                          data-tracking-actionoutcome="advance"
                          data-tracking-element="button"
                        >
                          Avançar
                        </Button>
                      </div>
                    )}
                  </>
                }
                renderElse={
                  <>
                    {renderFirstStep()}
                    {renderSecondStep()}
                  </>
                }
              />

              <div ref={formSecondStep} className="register-form-content">
                <If condition={isHeroFormOpen} renderIf={renderSecondStep()} />
              </div>
            </Form>
          )
        }}
      </Formik>
      <If
        condition={!isRegisterPage}
        renderIf={
          isHeroFormOpen && (
            <Accordion
              title={'detalhes sobre a política'}
              trackingLocation="LPHeroHoliday"
              trackingPosition="form"
              trackingActionOutcome="clique"
              trackingElement="button"
              trackingText={'detalhes sobre a política'}
              dataUnique={'05a990ec-2355-4142-aa46-3926cb9a94f5'}
            >
              {renderLegalBlok()}
            </Accordion>
          )
        }
        renderElse={renderLegalBlok()}
      />
    </div>
  )
}

export default LandingHeroForm
